/**
 * BulkPreviewModal — shows parsed rows + diff stats + Confirm import button.
 *
 * Receives the JSON returned by /api/bulk/{resource}/preview and renders:
 *   • header strip with stats (new / update / errors)
 *   • scrollable table with first-column status chip + row data
 *   • Confirm / Cancel actions, posting back to /api/bulk/{resource}/commit
 */
import { useState } from 'react';
import axios from 'axios';
import { X, CheckCircle2, AlertTriangle, Loader2, Upload, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  new:    { bg: '#16A34A15', fg: '#16A34A', label: 'New' },
  update: { bg: '#0EA5E915', fg: '#0EA5E9', label: 'Update' },
  error:  { bg: '#DC262615', fg: '#DC2626', label: 'Error' },
};

export default function BulkPreviewModal({ resource, data, color = '#42CDD7', onClose, onCommitted }) {
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const headers = data.headers || [];
  const rows = data.rows || [];
  const stats = data.stats || { total: 0, new: 0, update: 0, errors: 0 };

  const commit = async () => {
    setCommitting(true); setResult(null);
    try {
      const r = await axios.post(`${API}/bulk/${resource}/commit`,
        { rows: rows.filter(r => r._status !== 'error') },
        { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Commit failed' });
    }
    setCommitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`bulk-preview-modal-${resource}`}>
      <div className="absolute inset-0 bg-black/60" onClick={committing ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-['Outfit'] text-xl font-bold text-foreground">Preview import — {resource.replace(/_/g, ' ')}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {data.filename ? <><strong>{data.filename}</strong> · </> : null}
              {stats.total} row(s) parsed. Review before committing.
            </p>
          </div>
          <button onClick={onClose} disabled={committing} data-testid="bulk-preview-close" className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stats strip */}
        <div className="px-6 py-3 border-b border-border flex flex-wrap items-center gap-2 text-sm">
          <Pill color="#16A34A" label="New" value={stats.new} testid={`stat-new-${resource}`} />
          <Pill color="#0EA5E9" label="Updates" value={stats.update} testid={`stat-update-${resource}`} />
          <Pill color="#DC2626" label="Errors" value={stats.errors} testid={`stat-error-${resource}`} />
          <span className="text-muted-foreground text-xs ml-auto">{stats.total} total</span>
        </div>

        {data.errors && data.errors.length > 0 && (
          <div className="px-6 py-3 border-b border-border bg-red-500/5">
            <p className="text-xs font-bold uppercase tracking-wider text-red-600 mb-1">Parse warnings</p>
            <ul className="text-xs text-red-600 space-y-0.5">
              {data.errors.slice(0, 6).map((e, i) => <li key={i}>· {e}</li>)}
            </ul>
          </div>
        )}

        {/* Rows */}
        <div className="flex-1 overflow-auto px-6 py-3">
          {rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
              No rows parsed. Make sure your file's first row is a header that matches the resource columns
              (download the blank template from the Export menu for the exact column names).
            </div>
          ) : (
            <table className="w-full text-xs" data-testid={`bulk-preview-table-${resource}`}>
              <thead className="sticky top-0 bg-background">
                <tr>
                  <th className="text-left p-2 font-bold uppercase tracking-wider text-[10px] text-muted-foreground">Status</th>
                  {headers.map(h => (
                    <th key={h} className="text-left p-2 font-bold uppercase tracking-wider text-[10px] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const c = STATUS_COLOR[r._status] || STATUS_COLOR.update;
                  return (
                    <tr key={i} className="border-b border-border" data-testid={`bulk-preview-row-${i}`}>
                      <td className="p-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: c.bg, color: c.fg }}>{c.label}</span>
                        {r._error && <div className="text-[10px] text-red-500 mt-0.5">{r._error}</div>}
                      </td>
                      {headers.map(h => (
                        <td key={h} className="p-2 align-top max-w-[260px]">
                          <div className="truncate" title={r[h]}>{r[h] || <span className="text-muted-foreground italic">—</span>}</div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Result banner */}
        {result && (
          <div className="px-6 py-3 border-t border-border" data-testid={`bulk-result-${resource}`}>
            {result.error ? (
              <div className="text-sm text-red-600 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {result.error}
              </div>
            ) : (
              <div className="text-sm text-green-700 inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Import complete: <strong>{result.inserted} new</strong>, <strong>{result.updated} updated</strong>
                {result.error_count > 0 && <span className="text-red-600">, {result.error_count} errors</span>}
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        <div className="px-6 py-3 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} disabled={committing}
            data-testid="bulk-preview-cancel"
            className="text-sm font-semibold px-3 py-1.5 rounded-lg border border-border">
            {result && !result.error ? 'Done' : 'Cancel'}
          </button>
          {!result?.inserted && !result?.updated && (
            <button onClick={result?.error ? commit : commit} disabled={committing || stats.total === 0}
              data-testid="bulk-preview-commit"
              className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ backgroundColor: color }}>
              {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (result?.error ? <RotateCcw className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />)}
              {committing ? 'Importing…' : (result?.error ? 'Retry' : `Confirm import (${stats.total - stats.errors})`)}
            </button>
          )}
          {(result?.inserted || result?.updated) && (
            <button onClick={onCommitted} data-testid="bulk-preview-done"
              className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white"
              style={{ backgroundColor: color }}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Pill({ color, label, value, testid }) {
  return (
    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full" data-testid={testid}
      style={{ backgroundColor: `${color}15`, border: `1px solid ${color}30` }}>
      <span className="text-base font-bold tabular-nums" style={{ color }}>{value || 0}</span>
      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
    </div>
  );
}
